// --------------------------------------------------------------
// Created On: 2024-12-03
// Author: Zachary Thomas
//
// Last Modified: 2026-04-22
// Modified By: Dimitra Weinstein
//
// Copyright 2024 - 2026 © Cornell Pump Company, All Rights Reserved
// --------------------------------------------------------------

import React, { useState } from "react";
import Modal from "../../Modal/Modal";
import ModalHeader from "../../ModalHeader/ModalHeader";
import ModalBody from "../../ModalBody/ModalBody";
import ModalFooter from "../../ModalFooter/ModalFooter";
import Error from "../../Error/Error";
import Spinner from "../../Spinner/Spinner";
import InlineEllipsisSpinner from "../../InlineEllipsisSpinner/InlineEllipsisSpinner";
import DisplayDiagnosisStatus from "../../DisplayDiagnosisStatus/DisplayDiagnosisStatus";
import { API } from "../../../constants/miscellaneous";
import useReduxSelector from "../../../hooks/useReduxSelector";
import useApi from "../../../hooks/useApi";
import userHasPermission from "src/utilities/userHasPermission";
import { APPLY_FIRMWARE_PERMISSION } from "src/constants/permissions";
import ConfirmFirmwareUpdateModal from "../../ConfirmFirmwareUpdateModal/ConfirmFirmwareUpdateModal";
import styles from "./ValidateAssetModal.module.scss";

// Modal for validating that an asset is configured correctly.
export default function ValidateAssetModal(props: Props): Component {
  const currentUser = useReduxSelector((state) => state.currentUser);
  const [loading, setLoading] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [deviceShouldReport, setDeviceShouldReport] = useState<boolean>(false);
  const [deviceConnected, setDeviceConnected] = useState<boolean>(false);
  const [deviceIsOnLatestFirmware, setDeviceIsOnLatestFirmware] = useState<boolean>(false);
  const [controllerShouldReport, setControllerShouldReport] = useState<boolean>(false);
  const [controllerConnected, setControllerConnected] = useState<boolean>(false);
  const [analogSensors, setAnalogSensors] = useState<AnalogSensor[]>([]);
  const [diagnosisPerformed, setDiagnosisPerformed] = useState<boolean>(false);
  const [configurationTestCount, setConfigurationTestCount] = useState<number>(0);
  const [showConfirmUpdateFirmwareModal, setShowConfirmUpdateFirmwareModal] = useState<boolean>(false);
  const configurationIsBad =
    (deviceShouldReport && !deviceConnected) ||
    (controllerShouldReport && !controllerConnected) ||
    analogSensors.some((analogSensor) => !analogSensor.connected);

  // If we have found an asset, then validate connection information.
  useApi(
    () => {
      if (props.assetId !== null) {
        setLoading(true);
        return true;
      } else {
        setLoading(false);
        return false;
      }
    },
    {
      method: "POST",
      url: `${API}/company/${currentUser.companyId}/asset/${props.assetId === null ? 0 : props.assetId}/validate`,
    },
    async (response: Response, responseBody: ResponseBody) => {
      if (response.ok && responseBody) {
        setDeviceShouldReport(responseBody.deviceShouldReport);
        setDeviceConnected(responseBody.deviceConnected);
        setDeviceIsOnLatestFirmware(responseBody.deviceIsOnLatestFirmware);
        setControllerShouldReport(responseBody.controllerShouldReport);
        setControllerConnected(responseBody.controllerConnected);
        setAnalogSensors(responseBody.analogSensors);
        setDiagnosisPerformed(true);
        setErrorMessage("");
      } else {
        setErrorMessage(
          "Our attempt to communicate with the monitoring device took longer than expected. " +
            "Please check that the device is able to communicate and try again."
        );
        setDiagnosisPerformed(false);
      }
      setLoading(false);
    },
    [props.assetId, configurationTestCount]
  );

  // Close the child update confirmation modal and this current modal.
  function handleFirmwareUpdate(): void {
    setShowConfirmUpdateFirmwareModal(false);
    props.onClose();
  }

  return (
    <div>
      <Spinner loading={loading} />

      <Modal
        show={true}
        onHide={() => props.onClose()}
        backdropClassName={`${styles.modal} ${styles.backdrop}`}
        style={{ zIndex: "var(--modal-z-index)" }}
        size="lg"
        centered
        animation
      >
        <ModalHeader>
          <h5 className="font-weight-bold">Validate Asset</h5>
        </ModalHeader>

        <ModalBody>
          {loading && (
            <div>
              <div className="text-center">
                Checking Asset Configuration <InlineEllipsisSpinner loading={true} />
              </div>
            </div>
          )}

          {props.assetId !== null && !loading && diagnosisPerformed && (
            <div className="row justify-content-center px-5">
              <div className="col-8">
                <DisplayDiagnosisStatus
                  loading={false}
                  deviceType={props.deviceType}
                  deviceShouldReport={deviceShouldReport}
                  deviceConnected={deviceConnected}
                  deviceIsOnLatestFirmware={deviceIsOnLatestFirmware}
                  controllerShouldReport={controllerShouldReport}
                  controllerConnected={controllerConnected}
                  analogSensors={analogSensors}
                />
              </div>
            </div>
          )}

          {props.assetId !== null && !loading && configurationIsBad && (
            <div className="text-center">
              <button
                className="btn btn-primary text-center mx-auto my-2"
                data-test="retry-asset-diagnosis-button"
                type="button"
                onClick={() => setConfigurationTestCount((prev) => prev + 1)}
              >
                Retry Validation Check
              </button>
            </div>
          )}

          {errorMessage.length > 0 && (
            <div className="m-3">
              <Error message={errorMessage} />
            </div>
          )}
        </ModalBody>

        <ModalFooter className={styles.footer}>
          {userHasPermission([[APPLY_FIRMWARE_PERMISSION]]) &&
            deviceShouldReport === true &&
            deviceIsOnLatestFirmware === false && (
              <button
                className={`${styles.btn} btn btn-primary`}
                type="button"
                onClick={() => setShowConfirmUpdateFirmwareModal(true)}
              >
                Close and Apply Firmware
              </button>
            )}
          <button className={`${styles.btn} btn btn-secondary`} type="button" onClick={() => props.onClose()}>
            Close
          </button>
        </ModalFooter>
      </Modal>

      <ConfirmFirmwareUpdateModal
        showModal={showConfirmUpdateFirmwareModal}
        deviceId={props.deviceId}
        onClose={() => setShowConfirmUpdateFirmwareModal(false)}
        onUpdate={() => handleFirmwareUpdate()}
        onCancel={() => setShowConfirmUpdateFirmwareModal(false)}
      />
    </div>
  );
}

interface Props {
  assetId: number | null;
  deviceId: number | null;
  deviceType: string | null;
  onClose: () => void;
}

interface ResponseBody {
  deviceShouldReport: boolean;
  deviceConnected: boolean;
  deviceIsOnLatestFirmware: boolean;
  controllerShouldReport: boolean;
  controllerConnected: boolean;
  analogSensors: AnalogSensor[];
}

interface AnalogSensor {
  position: number;
  connected: boolean;
}
